import React, { useEffect, useState } from 'react'
import {
  Container,
  ListContainer,
  TextBody,
  FieldText,
  FieldHidden,
  TextCaption,
  SelectAutocomplete,
  Icon,
  RadioGroup,
  Checkbox
} from '@pmi/pmi-iqos-ds-react'
import {
  registerParam,
  unegisterParam,
  validateParam,
  constants,
  htmlTranslation
} from '@pmi/pmi-iqos-shared-react'
import { DcxApiManager } from '@dcx-common/services'

const CheckoutShipping = ({
  translations,
  toggleNewShippingAddress,
  hasTobacco,
  isDefaultAddressValid,
  handleChangeShippingAddressType,
  handleChangeInputsNewShippingAddress,
  shippingAddress,
  newShippingAddress,
  showSaveAsProfileAddress,
  handleChangeZipCode,
  handleEditTVAClick,
  handleChangeSaveAsProfileAddress,
  toggleSaveAsProfileAddress,
  isDefaultAddressCheckboxDisabled,
  formId,
  loading,
  isSagawa,
  zipCodeErrorMessage,
  zipCodeEligibleForSagawa,
  line
}) => {
  const [postalCodeErrors, setPostalCodeErrors] = useState([])
  const [cityErrors, setCityErrors] = useState([])
  const [stateErrors, setStateErrors] = useState([])
  const [addressLineErrors, setAddressLineErrors] = useState([])

  const api = new DcxApiManager(line)

  useEffect(() => {
    if (newShippingAddress.city !== '') {
      const errors = handleChangeInput('city', newShippingAddress.city)
      setCityErrors(errors)
    }
  }, [newShippingAddress])

  useEffect(() => {
    if (isSagawa && newShippingAddress.postalCode) {
      if (!zipCodeEligibleForSagawa) {
        setPostalCodeErrors([''])
        setStateErrors([''])
        setCityErrors([''])
      } else {
        setPostalCodeErrors([])
        setStateErrors([])
      }
    }
  }, [zipCodeEligibleForSagawa, newShippingAddress])

  const formFieldNames = {
    postalCode: 'postalCode',
    city: 'city',
    state: 'state',
    addressLine1: 'addressLine1'
  }

  const validators = {
    [formFieldNames.postalCode]: registerParam(
      [],
      false,
      formId,
      newShippingAddress.postalCode,
      errors => {
        setPostalCodeErrors(errors)
      }
    ),
    [formFieldNames.city]: registerParam(
      [
        {
          type: 'addressCharactersValidation',
          rule: constants.ADDRESS_REGEX_VALIDATION,
          description: translations.tk_checkoutAddressCharactersValidation
        },
        {
          type: 'length',
          rule: 'max_45',
          description: translations.tk_characterExceeded
        },
        {
          type: 'contains',
          rule: ',',
          description: translations.tk_cityAddressInvalidComma
        }
      ],
      false,
      formId,
      newShippingAddress.city,
      errors => {
        setCityErrors(errors)
      }
    ),
    [formFieldNames.state]: registerParam(
      [],
      false,
      formId,
      newShippingAddress.state,
      errors => {
        setStateErrors(errors)
      }
    ),
    [formFieldNames.addressLine1]: registerParam(
      [
        {
          type: 'addressCharactersValidation',
          rule: constants.ADDRESS_REGEX_VALIDATION,
          description: translations.tk_checkoutAddressCharactersValidation
        },
        {
          type: 'length',
          rule: 'max_45',
          description: translations.tk_characterExceeded
        },
        {
          type: 'contains',
          rule: ',',
          description: translations.tk_buildingNameInvalidComma
        }
      ],
      true,
      formId,
      newShippingAddress.addressLine1,
      errors => {
        setAddressLineErrors(errors)
      }
    )
  }

  useEffect(() => {
    return () => {
      Object.keys(formFieldNames).forEach(fieldNameKey =>
        unegisterParam(validators[formFieldNames[fieldNameKey]])
      )
    }
  }, [])

  const handleChangeCity = e => {
    handleChangeInputsNewShippingAddress(e)
    const errors = handleChangeInput(e.target.name, e.target.value)
    setCityErrors(errors)
  }

  const handleChangeAddressLine = e => {
    handleChangeInputsNewShippingAddress(e)
    const errors = handleChangeInput(e.target.name, e.target.value)
    setAddressLineErrors(errors)
  }

  const handleChangeInput = (name, value) => {
    const errors = validateParam(value, validators[name], formId)
    return errors
  }

  const formattedZipCode = postalCode => {
    return '〒' + postalCode.substring(0, 3) + '-' + postalCode.substring(3, 7)
  }

  const formattedShippingAddress = address => {
    return (
      address &&
      `${formattedZipCode(address.postalCode)} ${address.state} ${
        address.city
      } ${address.addressLine1}`
    )
  }

  const promiseOptions = zipcode =>
    zipcode && zipcode.length > 2
      ? api.auth
          .getAddressInfo({
            params: {
              ...(isSagawa && { s: true }),
              zipcode
            }
          })
          .then(res => {
            const results = res.zip_codes.map(item => {
              return {
                value: item.zip_code,
                label:
                  item.zip_code +
                  ' ' +
                  item.state +
                  ' ' +
                  item.city +
                  ' ' +
                  item.village,
                item: item
              }
            })
            return results
          })
      : Promise.resolve([])

  const renderShippingAddressSelection = () => {
    const addressTypes = [
      {
        value: 0,
        content: (
          <Container>
            <TextBody
              size={TextBody.sizes.s}
              {...htmlTranslation(
                translations,
                'tk_checkoutDefaultAddressRadio'
              )}
            />
            <TextCaption size={TextBody.sizes.s}>
              {formattedShippingAddress(shippingAddress)}
            </TextCaption>
          </Container>
        )
      },
      {
        value: 1,
        content: (
          <Container
            fullHeight
            display={Container.displays.flex}
            justifyContent={Container.justifyContentValues.between}
            alignItems={Container.alignItemsValues.center}>
            <Container>
              <Container
                display={[Container.displays.block, Container.displays.none]}>
                <TextBody
                  size={TextBody.sizes.s}
                  {...htmlTranslation(
                    translations,
                    'tk_checkoutEnterAddressRadio'
                  )}
                />
              </Container>
              <Container
                display={[Container.displays.none, Container.displays.block]}>
                <TextCaption size={TextBody.sizes.s}>
                  {translations.tk_checkoutEnterAddressRadio}
                </TextCaption>
              </Container>
            </Container>
            <Container
              display={[Container.displays.none, Container.displays.block]}>
              <Icon size={Icon.sizes.xs} image={Icon.images.address} />
            </Container>
          </Container>
        )
      }
    ]
    return (
      <Container pt="4" pb="8" px={['0', '8']}>
        <Container px={['8', '0']}>
          <RadioGroup
            alternative
            name="shippingAddressType"
            label=""
            style={[RadioGroup.styles.default, RadioGroup.styles.square]}
            columns={[1, 2]}
            items={addressTypes}
            getItemValue={(item, index) => addressTypes[index].value}
            getItemContent={(item, index) => addressTypes[index].content}
            isItemSelected={(item, index) =>
              addressTypes[index].value === 0
                ? !toggleNewShippingAddress
                : toggleNewShippingAddress
            }
            onChangeHandler={handleChangeShippingAddressType}
            data-testing="Checkout_RadioGroup_ShippingAddressType"
          />
        </Container>
        {showSaveAsProfileAddress &&
          !toggleNewShippingAddress &&
          !isDefaultAddressCheckboxDisabled && (
            <Container pt="6">
              <Checkbox
                checked={toggleSaveAsProfileAddress}
                name={'toggleSaveAsProfileAddress'}
                label={translations.tk_checkoutSaveAsProfileAddress}
                onChangeHandler={handleChangeSaveAsProfileAddress}
                data-testing="Checkout_Checkbox_SaveDeliveryAsProfileAddress"
              />
            </Container>
          )}
      </Container>
    )
  }

  const renderShippingAddressWithTobacco = () => {
    const addressTypes = [
      {
        value: 0,
        content: (
          <Container
            fullHeight
            display={Container.displays.flex}
            justifyContent={Container.justifyContentValues.between}
            alignItems={Container.alignItemsValues.center}>
            <Container>
              <TextBody
                size={TextBody.sizes.s}
                {...htmlTranslation(
                  translations,
                  'tk_checkoutDefaultAddressRadio'
                )}
              />
              <TextCaption size={TextBody.sizes.s}>
                {formattedShippingAddress(shippingAddress)}
              </TextCaption>
            </Container>
            <Container onClickHandler={handleEditTVAClick}>
              <Icon size={Icon.sizes.xs} image={Icon.images.address} />
            </Container>
          </Container>
        )
      }
    ]
    return (
      <Container
        pt="4"
        pb="8"
        px="8"
        display={Container.displays.flex}
        justifyContent={Container.justifyContentValues.center}
        alignItems={Container.alignItemsValues.center}>
        <Container width={['full', 'full', '1/2']}>
          <RadioGroup
            alternative
            name="shippingAddressType"
            label=""
            style={RadioGroup.styles.square}
            columns={1}
            items={addressTypes}
            getItemValue={(item, index) => addressTypes[index].value}
            getItemContent={(item, index) => addressTypes[index].content}
            isItemSelected={(item, index) => addressTypes[index].value === 0}
            onChangeHandler={() => {}}
            data-testing="Checkout_RadioGroup_ShippingAddressType"
          />
        </Container>
      </Container>
    )
  }

  const renderNewShippingAddressForm = () => {
    return (
      <Container px={['0', '8']} pb={['10', '8']}>
        <ListContainer
          columns={[1, 2]}
          xBetween="8"
          yBetween="5"
          overflow="visible">
          <SelectAutocomplete
            name={formFieldNames.postalCode}
            label={translations.tk_fieldName_zipCode}
            placeholder={translations.tk_fieldPlaceholder_zipCode}
            value={newShippingAddress.postalCode}
            onChangeHandler={handleChangeZipCode}
            loadOptions={promiseOptions}
            errors={zipCodeErrorMessage || postalCodeErrors}
            disabled={loading}
            data-testing="Checkout_SelectAutocomplete_ZipCode"
          />
          <FieldText
            name={formFieldNames.state}
            label={translations.tk_fieldName_state}
            value={newShippingAddress.state}
            onChangeHandler={handleChangeInputsNewShippingAddress}
            errors={stateErrors}
            disabled
            data-testing="Checkout_FieldText_State"
          />
          <FieldText
            name={formFieldNames.city}
            label={translations.tk_fieldName_city}
            value={newShippingAddress.city}
            onChangeHandler={handleChangeCity}
            errors={cityErrors}
            disabled={loading}
            data-testing="Checkout_FieldText_City"
          />
          <FieldText
            name={formFieldNames.addressLine1}
            label={translations.tk_fieldName_address}
            value={newShippingAddress.addressLine1}
            onChangeHandler={handleChangeAddressLine}
            errors={addressLineErrors}
            disabled={loading}
            data-testing="Checkout_FieldText_AddressLine1"
          />
        </ListContainer>
        <FieldHidden
          value={newShippingAddress.postalCodeHidden}
          name="postalCodeHidden"
        />
        {showSaveAsProfileAddress && (
          <Container pt="6">
            <Checkbox
              checked={toggleSaveAsProfileAddress}
              name={'toggleSaveAsProfileAddress'}
              label={translations.tk_checkoutSaveAsProfileAddress}
              onChangeHandler={handleChangeSaveAsProfileAddress}
              data-testing="Checkout_Checkbox_SaveDeliveryAsProfileAddress"
            />
          </Container>
        )}
      </Container>
    )
  }

  return (
    <Container>
      {hasTobacco ? (
        renderShippingAddressWithTobacco()
      ) : (
        <Container
          pt={
            !isDefaultAddressValid && toggleNewShippingAddress
              ? ['10', '6']
              : '0'
          }>
          {isDefaultAddressValid && renderShippingAddressSelection()}
          {toggleNewShippingAddress && renderNewShippingAddressForm()}
        </Container>
      )}
    </Container>
  )
}

export default CheckoutShipping
